/** Tokens visuais — menu lateral Klinvia (shadcn sidebar-07 + klinvia-tokens) */
export const SIDEBAR_THEME = {
  paper: '#FFFFFF',
  muted: '#F1F5F9',
  mutedForeground: '#64748B',
  navy: '#0F172A',
  slate: '#64748B',
  slateMid: '#475569',
  /** Texto de itens inativos — mais escuro que slate para leitura confortável */
  menuText: '#334155',
  slateFaint: '#94A3B8',
  border: 'rgba(13, 148, 136, 0.14)',
  borderSoft: '#E2E8F0',
  teal: '#0D9488',
  tealSoft: 'rgba(13, 148, 136, 0.08)',
  primary: '#2563EB',
  primarySoft: 'rgba(37, 99, 235, 0.08)',
  primaryBorder: 'rgba(37, 99, 235, 0.18)',
  gridLine: 'rgba(100, 116, 139, 0.06)',
  shadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 8px 24px rgba(15, 23, 42, 0.06)',
  radius: 12,
  radiusSm: 10,
  /** Só propriedades de pintura: animar margem/padding de cada item custa layout por frame. */
  transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
  widthTransition: 'width 0.22s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.22s ease',
  logoOpacity: 0.72,
  logoOpacityHover: 0.9,
  logoExpandedMaxHeight: 64,
  logoExpandedMaxWidth: 140,
  logoCollapsedMaxHeight: 30,
  logoCollapsedMaxWidth: 44,
  /** Centralização óptica no menu recolhido (compensa paddingRight do drawer). */
  logoCollapsedTranslateX: 6,
} as const;

export const sidebarClinicalGridBg = {
  backgroundColor: SIDEBAR_THEME.paper,
  backgroundImage: `
    linear-gradient(${SIDEBAR_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${SIDEBAR_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const sidebarDrawerInnerSx = {
  ...sidebarClinicalGridBg,
  position: 'relative' as const,
  height: '100%',
  maxHeight: '100%',
  overflowX: 'visible',
  overflowY: 'hidden',
  display: 'flex',
  flexDirection: 'column' as const,
  paddingRight: '8px',
  paddingTop: { xs: 'env(safe-area-inset-top, 0px)', md: 0 },
  paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', md: 0 },
  transition: SIDEBAR_THEME.transition,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '2px',
    height: '100%',
    background: `linear-gradient(180deg, ${SIDEBAR_THEME.teal}33 0%, ${SIDEBAR_THEME.teal}14 50%, ${SIDEBAR_THEME.teal}33 100%)`,
    pointerEvents: 'none',
    zIndex: 1,
  },
};

export const sidebarDesktopPaperSx = (
  drawerOpen: boolean,
  bannerHeight: number,
  drawerMarginVertical: number,
  drawerMarginLeft: number,
  theme: { zIndex: { drawer: number } },
  drawerWidth: number,
  drawerWidthCollapsed: number,
  hoverFlyout = false,
) => ({
  boxSizing: 'border-box' as const,
  width: drawerOpen ? drawerWidth : drawerWidthCollapsed,
  height: `calc(var(--ui-viewport-height, 100dvh) - ${bannerHeight + drawerMarginVertical * 2}px)`,
  maxHeight: `calc(var(--ui-viewport-height, 100dvh) - ${bannerHeight + drawerMarginVertical * 2}px)`,
  overflowX: 'visible',
  overflowY: 'hidden',
  backgroundColor: SIDEBAR_THEME.paper,
  border: `1px solid ${SIDEBAR_THEME.borderSoft}`,
  borderRight: 'none',
  boxShadow: drawerOpen && hoverFlyout
    ? '0 16px 48px rgba(15, 23, 42, 0.14), 0 4px 16px rgba(15, 23, 42, 0.08)'
    : SIDEBAR_THEME.shadow,
  transition: `${SIDEBAR_THEME.widthTransition} !important`,
  willChange: 'width',
  /** Relayout da animação fica restrito ao menu. */
  contain: 'layout style',
  marginLeft: `${drawerMarginLeft}px`,
  marginTop: `${bannerHeight + drawerMarginVertical}px`,
  marginBottom: `${drawerMarginVertical}px`,
  borderRadius: `${SIDEBAR_THEME.radius}px 0 0 ${SIDEBAR_THEME.radius}px`,
  position: 'relative' as const,
  zIndex: drawerOpen && hoverFlyout ? theme.zIndex.drawer + 3 : theme.zIndex.drawer,
  display: 'flex',
  flexDirection: 'column' as const,
});

export function sidebarMenuButtonSx(opts: {
  isActive: boolean;
  drawerOpen: boolean;
  nested?: boolean;
}) {
  const { isActive, drawerOpen, nested } = opts;
  return {
    position: 'relative' as const,
    borderRadius: `${nested ? 8 : SIDEBAR_THEME.radiusSm}px`,
    mx: drawerOpen ? (nested ? 0 : 1) : 'auto',
    my: nested ? 0.15 : 0.35,
    /** Topo: ícone expandido ~na mesma coluna do recolhido (evita “pulo” na animação). */
    px: drawerOpen ? (nested ? 1.25 : 2) : 0.75,
    py: drawerOpen ? (nested ? 0.55 : 0.75) : 0.65,
    minHeight: nested ? 34 : 38,
    justifyContent: drawerOpen ? 'flex-start' : 'center',
    width: drawerOpen ? 'auto' : 'fit-content',
    color: isActive ? SIDEBAR_THEME.primary : SIDEBAR_THEME.menuText,
    bgcolor: isActive ? SIDEBAR_THEME.primarySoft : 'transparent',
    border: `1px solid ${isActive ? SIDEBAR_THEME.primaryBorder : 'transparent'}`,
    transition: SIDEBAR_THEME.transition,
    overflow: drawerOpen ? (nested ? 'visible' : 'hidden') : 'visible',
    minWidth: 0,
    maxWidth: '100%',
    ...(nested && drawerOpen && {
      pr: 1,
      gap: 0.5,
    }),
    ...(!drawerOpen && {
      ml: 'auto',
      mr: 'auto',
      display: 'flex',
      alignItems: 'center',
    }),
    '&:hover': {
      bgcolor: isActive ? 'rgba(37, 99, 235, 0.12)' : SIDEBAR_THEME.muted,
      color: isActive ? SIDEBAR_THEME.primary : SIDEBAR_THEME.navy,
      borderColor: isActive ? SIDEBAR_THEME.primaryBorder : SIDEBAR_THEME.borderSoft,
    },
    '&::before': isActive
      ? {
          content: '""',
          position: 'absolute',
          left: nested ? 6 : 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 3,
          height: '62%',
          bgcolor: SIDEBAR_THEME.teal,
          borderRadius: '0 2px 2px 0',
        }
      : {},
  };
}

export function sidebarMenuIconSx(isActive: boolean, drawerOpen: boolean, collapsedSize = '1.5rem') {
  return {
    color: isActive ? `${SIDEBAR_THEME.primary} !important` : SIDEBAR_THEME.menuText,
    minWidth: drawerOpen ? (collapsedSize === '1.5rem' ? 36 : 28) : 'auto',
    justifyContent: drawerOpen ? 'flex-start' : 'center',
    overflow: drawerOpen ? 'hidden' : 'visible',
    transition: SIDEBAR_THEME.transition,
    ...(!drawerOpen
      ? {
          '& .MuiSvgIcon-root': {
            fontSize: collapsedSize,
            color: isActive ? `${SIDEBAR_THEME.primary} !important` : SIDEBAR_THEME.menuText,
          },
        }
      : {
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
            color: isActive ? `${SIDEBAR_THEME.primary} !important` : SIDEBAR_THEME.menuText,
          },
        }),
  };
}

export const sidebarMenuTextSx = (isActive: boolean) => ({
  margin: 0,
  padding: 0,
  overflow: 'hidden !important',
  textOverflow: 'ellipsis !important',
  whiteSpace: 'nowrap !important',
  flex: '1 1 auto',
  minWidth: 0,
  maxWidth: '100%',
  '& .MuiListItemText-primary': {
    color: isActive ? `${SIDEBAR_THEME.primary} !important` : SIDEBAR_THEME.menuText,
    fontWeight: isActive ? 600 : 500,
    fontSize: '0.8125rem',
    letterSpacing: '-0.01em',
    margin: 0,
    padding: 0,
    lineHeight: 1.45,
    overflow: 'hidden !important',
    textOverflow: 'ellipsis !important',
    whiteSpace: 'nowrap !important',
  },
});

export const sidebarSubmenuListSx = {
  width: '100%',
  flexDirection: 'column' as const,
  position: 'relative' as const,
  zIndex: 1,
  ml: 2,
  mr: 0.75,
  my: 0.5,
  pl: 1.25,
  pr: 0.75,
  py: 0.25,
  borderLeft: `2px solid ${SIDEBAR_THEME.borderSoft}`,
  bgcolor: 'transparent',
  overflow: 'visible',
};

export const sidebarFooterSx = {
  flexShrink: 0,
  minHeight: 'fit-content',
  borderTop: `1px solid ${SIDEBAR_THEME.borderSoft}`,
  bgcolor: SIDEBAR_THEME.muted,
  backgroundImage: `linear-gradient(to top, ${SIDEBAR_THEME.tealSoft}, ${SIDEBAR_THEME.muted})`,
};

export const sidebarUserRowSx = (drawerOpen: boolean) => ({
  px: drawerOpen ? 1.5 : 1,
  py: 1,
  mx: drawerOpen ? 1 : 0.5,
  mb: 0.5,
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  justifyContent: drawerOpen ? 'flex-start' : 'center',
  cursor: 'pointer',
  borderRadius: `${SIDEBAR_THEME.radiusSm}px`,
  border: `1px solid ${SIDEBAR_THEME.borderSoft}`,
  bgcolor: SIDEBAR_THEME.paper,
  transition: SIDEBAR_THEME.transition,
  '&:hover': {
    bgcolor: SIDEBAR_THEME.primarySoft,
    borderColor: SIDEBAR_THEME.primaryBorder,
  },
});

/** Linha nome + contador colados (submenu inbox) */
export const sidebarSubmenuLabelRowSx = {
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  gap: 0.5,
};

export function sidebarSubmenuLabelTextSx(isActive: boolean) {
  return {
    flex: '0 1 auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    color: isActive ? SIDEBAR_THEME.primary : SIDEBAR_THEME.menuText,
    fontWeight: isActive ? 600 : 500,
    fontSize: '0.8125rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.45,
  };
}

/** Badge no menu recolhido — mantém o contador dentro da faixa do ícone (evita corte no overflow). */
export type SidebarUnreadBadgeTone = 'error' | 'warning';

const SIDEBAR_UNREAD_BADGE_BG: Record<SidebarUnreadBadgeTone, string> = {
  error: '#EF4444',
  warning: '#EAB308',
};

export function sidebarCollapsedUnreadBadgeSx(
  unread: number,
  tone: SidebarUnreadBadgeTone = 'error',
) {
  const wide = unread >= 10;
  const bg = SIDEBAR_UNREAD_BADGE_BG[tone];
  return {
    flexShrink: 0,
    overflow: 'visible',
    '& .MuiBadge-badge': {
      fontSize: wide ? '0.5625rem' : '0.65rem',
      fontWeight: 700,
      height: wide ? 17 : 18,
      minWidth: wide ? 22 : 18,
      px: wide ? '5px' : '4px',
      top: 1,
      right: 1,
      transform: 'scale(1) translate(15%, -20%)',
      boxSizing: 'border-box',
      ...(tone === 'warning'
        ? { bgcolor: bg, color: '#422006' }
        : {}),
    },
  };
}

export function sidebarUnreadPillSx(tone: SidebarUnreadBadgeTone = 'error') {
  const bg = SIDEBAR_UNREAD_BADGE_BG[tone];
  return {
    ml: 0.35,
    mr: 0.25,
    px: 0.55,
    py: 0.15,
    borderRadius: '8px',
    bgcolor: bg,
    color: tone === 'warning' ? '#422006' : '#FFFFFF',
    fontSize: '0.62rem',
    fontWeight: 700,
    lineHeight: 1.2,
    flexShrink: 0,
    minWidth: 18,
    textAlign: 'center' as const,
  };
}

/** Contador colado ao nome do subitem (ex.: inbox) */
export const sidebarSubmenuUnreadPillSx = {
  ...sidebarUnreadPillSx('error'),
  ml: 0,
  mr: 0,
  flexShrink: 0,
};

export const sidebarCollapseBtnSx = {
  position: 'absolute' as const,
  top: 12,
  right: -12,
  zIndex: 20,
  width: 24,
  height: 24,
  bgcolor: SIDEBAR_THEME.paper,
  border: `1px solid ${SIDEBAR_THEME.borderSoft}`,
  boxShadow: SIDEBAR_THEME.shadow,
  color: SIDEBAR_THEME.menuText,
  transition: SIDEBAR_THEME.transition,
  '&:hover': {
    bgcolor: SIDEBAR_THEME.muted,
    color: SIDEBAR_THEME.navy,
    borderColor: SIDEBAR_THEME.border,
  },
};
